import React, { useEffect, useState, useContext } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { DatoCmsFooter, NavigationBlock } from '../../schema/datocms/footer';
import { DatoCmsHoursOfOperation, DatoCmsLink, DatoCmsContactInfo } from '../../schema/datocms/blocks';
import Link from '../link/link';
import SocialIcons from '../social-icons/social-icons';
import IconWithText from '../icon/icon-with-text';
import Accordion from '../accordion/accordion';
import AccordionContext from '../accordion/accordion.context';
import useViewport from '../../hooks/useViewport';
import StructuredText from './../structured-text/structured-text';
import ContactLink from '../contact-link/contact-link';

import CertificationSeals from './certification-seals';

const FooterNavGroup = ({ navGroup, header }: { navGroup: Array<NavigationBlock>, header: string }): React.ReactElement | null => {

    const [isMobileSize, setIsMobileSize] = useState(false);

    const { viewportWidth } = useViewport();

    const { setActiveGroup } = useContext(AccordionContext);

    useEffect(() => {
        if (viewportWidth < 1024) {
            setIsMobileSize(true);
        }
        else {
            setIsMobileSize(false);
            setActiveGroup && setActiveGroup(null);
        }
    }, [viewportWidth, setActiveGroup]);

    if (navGroup?.length <= 0) {
        return null;
    }

    const navigationMapper = (nav: NavigationBlock, index: number, className: string): React.ReactElement | null => {
        let navItem: React.ReactElement | null;
        const classList = 'flex-auto font-sans text-lg lg:text-base text-left text-white hover:underline mx-8 lg:mx-0 lg:w-auto py-2 lg:p-0';

        switch (nav.__typename) {
            case 'DatoCmsLink': {
                const link: DatoCmsLink = nav as DatoCmsLink;
                navItem = <Link internalPageReference={link.internalPageReference} internalPageSectionId={link.internalSectionComponentId} externalPageUrl={link.externalPageUrl} className={classList}>{link.displayText}</Link>;
            } break;
            case 'DatoCmsContactInfo': {
                const { iconName, contact } = nav as DatoCmsContactInfo;
                navItem = <ContactLink iconName={iconName} {...contact} className={classList} />;
            } break;
            case 'DatoCmsSocialMediaBlock':
                navItem = <SocialIcons className='hidden lg:flex text-white text-xl my-3' />;
                break;
            case 'DatoCmsHoursOfOperation':
                const { iconName, displayText } = nav as DatoCmsHoursOfOperation;
                navItem = <IconWithText iconName={iconName} text={displayText} className={classList} />;
                break;
            default:
                console.warn(`Unknown Navigation element: ${nav.__typename}`);
                navItem = null;
                break;
        }

        return navItem !== null ?
            <li key={index} className={className}>{navItem}</li> :
            null;
    };

    return isMobileSize ? (
        <Accordion
            title={header}
            classes={{
                details: 'w-full select-none border-b border-white',
                summary: 'justify-between px-8 focus:outline-white',
                summaryText: 'text-white py-5 uppercase font-bold text-lg',
                icon: 'text-white fill-current w-12 h-12'
            }}>
            {/* See footer.css for additional styles */}
            <ul className='mobile-nav-list bg-secondary'>
                {
                    navGroup.map((nav, index) => navigationMapper(nav, index, 'flex focus-within:outline-white'))
                }
            </ul>
        </Accordion>
    ) : (
        <div className="max-w-1/5">
            <h3 className="text-white uppercase text-xs font-sans pb-2">{header}</h3>
            <ul>
                {
                    navGroup.map((nav, index) => navigationMapper(nav, index, 'py-2'))
                }
            </ul>
        </div>
    );
};

const Footer = (): React.ReactElement => {

    const { footer } = useStaticQuery<FooterQueryResult>(FooterQuery);

    const [activeGroup, setActiveGroup] = useState<string | null>(null);

    return (
        <footer className="flex flex-col justify-center items-center bg-primary lg:px-12 pt-24 pb-8">
            <nav role="navigation" className="flex flex-row flex-wrap justify-between w-full xl:max-w-screen-xl">
                <AccordionContext.Provider value={{ activeGroup, setActiveGroup }}>
                    <FooterNavGroup header="Company" navGroup={footer.companyGroup} />
                    <FooterNavGroup header="Plans" navGroup={footer.plansGroup} />
                    <FooterNavGroup header="Resources" navGroup={footer.resourcesGroup} />
                    <FooterNavGroup header="Claims" navGroup={footer.claimsGroup} />
                    <FooterNavGroup header="Contact" navGroup={footer.contactGroup} />
                </AccordionContext.Provider>
            </nav>

            <div className="flex flex-col justify-center items-center px-8 lg:p-0 mb-12">
                <SocialIcons className='lg:hidden text-white text-3xl my-12 space-x-10' />

                <span className="text-center text-white mb-8 lg:mt-12 text-xs">&copy; {footer.copyrightText}, {footer.copyrightStartYear} - {new Date().getFullYear()}</span>

                <StructuredText
                    className="text-white text-xs w-full xl:max-w-screen-xl"
                    data={footer.disclaimer} />
            </div>

            <CertificationSeals />
        </footer>
    );
};

export default Footer;

interface FooterQueryResult {
    footer: DatoCmsFooter;
}

const FooterQuery = graphql`
    query FooterQuery {
        footer: datoCmsFooter {
            copyrightText
            copyrightStartYear
            disclaimer {
                blocks
                links {
                    __typename
                    ... on DatoCmsPage {
                        id: originalId
                        slug
                    }
                    ...ContactFragment
                }
                value
            }
            companyGroup {
                __typename
                ...LinkFragment
                ...SocialMediaBlockFragment
                ...HoursFragment
                ...ContactInfoFragment
            }
            plansGroup {
                __typename
                ...LinkFragment
                ...SocialMediaBlockFragment
                ...HoursFragment
                ...ContactInfoFragment
            }
            resourcesGroup {
                __typename
                ...LinkFragment
                ...SocialMediaBlockFragment
                ...HoursFragment
                ...ContactInfoFragment
            }
            claimsGroup {
                __typename
                ...LinkFragment
                ...SocialMediaBlockFragment
                ...HoursFragment
                ...ContactInfoFragment
            }
            contactGroup {
                __typename
                ...LinkFragment
                ...SocialMediaBlockFragment
                ...HoursFragment
                ...ContactInfoFragment
            }
        }
    }
`;
